/**
 * Drawer chart — TradingView lightweight-charts (pinned 4.2.0).
 */
import React, { useEffect, useRef, useState } from "react";
import { createChart, ColorType, CrosshairMode } from "lightweight-charts";
import { emaSeries } from "./movingAverages.js";

const CHART_PREF_KEY = "auctus_ma_chart_toggles";

function loadToggles() {
  try {
    const raw = localStorage.getItem(CHART_PREF_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { ema20: true, ema50: true, ema200: true };
}

function saveToggles(t) {
  try {
    localStorage.setItem(CHART_PREF_KEY, JSON.stringify(t));
  } catch {}
}

function barsForRange(bars, rangeKey) {
  if (!bars?.length) return bars;
  const n =
    rangeKey === "3M" ? 63 : rangeKey === "6M" ? 126 : rangeKey === "1Y" ? 252 : 126;
  return bars.slice(0, Math.min(n, bars.length));
}

export function TickerMaChart({ symbol, result, T, height = 320 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({});
  const [bars, setBars] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("6M");
  const [toggles, setToggles] = useState(loadToggles);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetch(`/api/chart-bars?symbol=${encodeURIComponent(symbol)}&limit=260`)
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled) {
          if (j.error) setErr(j.error);
          else setBars(j.bars || []);
        }
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.message || "load failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  useEffect(() => {
    saveToggles(toggles);
  }, [toggles]);

  useEffect(() => {
    if (!containerRef.current || !bars?.length) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = {};
    }

    const chart = createChart(containerRef.current, {
      height,
      layout: {
        background: { type: ColorType.Solid, color: T?.surface ?? "#0f172a" },
        textColor: T?.textSub ?? "#94a3b8",
      },
      grid: {
        vertLines: { color: `${T?.border ?? "#334155"}88` },
        horzLines: { color: `${T?.border ?? "#334155"}88` },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
    });
    chartRef.current = chart;

    const slice = barsForRange(bars, range);
    const chrono = [...slice].reverse();
    const candleData = chrono
      .filter((b) => b.t && b.c != null)
      .map((b) => ({
        time: typeof b.t === "number" ? b.t : Math.floor(new Date(b.t).getTime() / 1000),
        open: b.o,
        high: b.h,
        low: b.l,
        close: b.c,
      }))
      .sort((a, b) => a.time - b.time);

    const volData = chrono
      .filter((b) => b.t)
      .map((b) => ({
        time: typeof b.t === "number" ? b.t : Math.floor(new Date(b.t).getTime() / 1000),
        value: b.v || 0,
        color: b.c >= b.o ? `${T?.green ?? "#22c55e"}66` : `${T?.red ?? "#ef4444"}66`,
      }))
      .sort((a, b) => a.time - b.time);

    const candles = chart.addCandlestickSeries({
      upColor: T?.green ?? "#22c55e",
      downColor: T?.red ?? "#ef4444",
      borderVisible: false,
      wickUpColor: T?.green ?? "#22c55e",
      wickDownColor: T?.red ?? "#ef4444",
    });
    candles.setData(candleData);
    seriesRef.current.candles = candles;

    const vol = chart.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
    vol.setData(volData);

    const closesLatestFirst = slice.map((b) => b.c);
    const addEma = (period, color, key) => {
      const series = emaSeries(closesLatestFirst, period);
      if (!series) return;
      const line = chart.addLineSeries({ color, lineWidth: key === 50 ? 2 : 1, visible: toggles[key] });
      const data = chrono
        .map((b, i) => {
          const idx = slice.length - 1 - i;
          const v = series[idx];
          if (v == null || !b.t) return null;
          return {
            time: typeof b.t === "number" ? b.t : Math.floor(new Date(b.t).getTime() / 1000),
            value: v,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.time - b.time);
      line.setData(data);
      seriesRef.current[key] = line;
    };
    addEma(20, "#38bdf8", "ema20");
    addEma(50, "#f59e0b", "ema50");
    addEma(200, "#a855f7", "ema200");

    const addHLine = (price, color, title) => {
      if (price == null || !Number.isFinite(price)) return;
      const line = chart.addLineSeries({
        color,
        lineWidth: 1,
        lineStyle: 2,
        title,
        priceLineVisible: true,
        lastValueVisible: true,
      });
      if (candleData.length >= 2) {
        line.setData([
          { time: candleData[0].time, value: price },
          { time: candleData[candleData.length - 1].time, value: price },
        ]);
      }
    };
    addHLine(result?.stopPrice ?? result?.tradeStop, T?.red ?? "#ef4444", "Stop");
    addHLine(result?.t1, T?.green ?? "#22c55e", "T1");
    addHLine(result?.t2, T?.cyan ?? "#06b6d4", "T2");
    addHLine(result?.dipChecklist?.supportLevel, "#f97316", "Support");

    const cross = result?.ma?.lastCross;
    if (cross?.barsAgo != null && candleData.length > cross.barsAgo) {
      const idx = Math.max(0, candleData.length - 1 - cross.barsAgo);
      const t = candleData[idx]?.time;
      if (t) {
        candles.setMarkers([
          {
            time: t,
            position: cross.type === "GOLDEN" ? "belowBar" : "aboveBar",
            color: cross.type === "GOLDEN" ? T?.green : T?.red,
            shape: cross.type === "GOLDEN" ? "arrowUp" : "arrowDown",
            text: cross.type === "GOLDEN" ? "Golden" : "Death",
          },
        ]);
      }
    }

    chart.timeScale().fitContent();

    return () => {
      chart.remove();
      chartRef.current = null;
    };
  }, [bars, range, toggles, result, T, height]);

  useEffect(() => {
    for (const key of ["ema20", "ema50", "ema200"]) {
      seriesRef.current[key]?.applyOptions({ visible: toggles[key] });
    }
  }, [toggles]);

  const ma = result?.ma;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 6 }}>
        {["3M", "6M", "1Y"].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            style={{
              fontSize: 9,
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: 6,
              border: `1px solid ${range === r ? T.cyan : T.border}`,
              background: range === r ? `${T.cyan}22` : "transparent",
              color: range === r ? T.cyan : T.textDim,
              cursor: "pointer",
            }}
          >
            {r}
          </button>
        ))}
        {[
          ["ema20", "20", "#38bdf8"],
          ["ema50", "50", "#f59e0b"],
          ["ema200", "200", "#a855f7"],
        ].map(([key, label, color]) => (
          <label key={key} style={{ fontSize: 9, color: T.textSub, display: "flex", alignItems: "center", gap: 3 }}>
            <input
              type="checkbox"
              checked={!!toggles[key]}
              onChange={() => setToggles((t) => ({ ...t, [key]: !t[key] }))}
            />
            <span style={{ color }}>EMA{label}</span>
            {ma?.[`ema${label}`] != null && (
              <span style={{ fontFamily: "monospace" }}>${ma[`ema${label}`].toFixed(2)}</span>
            )}
          </label>
        ))}
      </div>
      {loading && <div style={{ fontSize: 10, color: T.textDim }}>Loading chart…</div>}
      {err && <div style={{ fontSize: 10, color: T.red }}>{err}</div>}
      <div ref={containerRef} style={{ width: "100%", minHeight: height }} />
    </div>
  );
}
